import Dexie, {Table} from 'dexie';
import {GetGcsServerOffset, InitiateGcsResumable, UploadGcsResumable} from "./Upload";
import {extractEnhancementAudioRendition, extractOpusAudioRendition} from "./Media";
import {CompositionService} from "../librettoEditor/services/CompositionService";
import {EngineInternalEventsEnum} from "../engine/EngineInternalEvents";
import { requestPersistentStorageOnce } from "../utils/storagePersistence";

type UploadState = {
    state: 'NOT_STARTED' | 'UPLOADING' | 'UPLOADED' | 'FAILED';
    uploadedBytes: number;
    totalBytes: number;
    sessionUrl?: string;   // resumable session URL
    updatedAt: number;
    uploadProgressPercent: number;
};

type AudioRenditionState = {
    state: | 'NOT_NEEDED'
        | 'NOT_STARTED'
        | 'EXTRACTING'
        | 'READY_LOCAL'
        | 'UPLOADING'
        | 'SYNCED'
        | 'FAILED';
    uploadedBytes: number;
    totalBytes: number;
    sessionUrl?: string;
    updatedAt: number;
    uploadProgressPercent: number;
}

type AssetRow = {
    assetId: string;
    assetType?: string;
    createTime: number;
    title: string;
    status?: string;
    projectId: string;
    thumbnailId?: string;
    userId: string;

    // local storage
    localPresent: boolean;         // true once OPFS write completes
    opfsPath: string;              // e.g. /assets/{assetId}/original
    objectUrl?: string;
    upload: UploadState;
    updatedAt: number;
};

type TrackRow = {
    assetId: string;
    audioBitrate?: number;
    createTime: number;
    deleted?: boolean;
    duration?: string;
    projectId: string;
    height?: number;
    width?: number;
    sampleRate?: number;
    title: string;
    trackId: string;
    trackType?: number;
    userId: string;
    videoBitrate?: number;

    // local storage
    localPresent: boolean;         // true once OPFS write completes
    opfsPath: string;              // e.g. /assets/{assetId}/original
    uploadOnly?: boolean;          // true for temporary local copies used only for resumable upload
    objectUrl?: string;
    upload: UploadState;
    updatedAt: number;
    mimeType?: string;             // original file MIME type (e.g., 'video/webm')
    originalFileName?: string;     // original file name with extension

    audioRenditionOpfsPath?: string;
    audioRenditionState?: AudioRenditionState;
    enhancementRenditionOpfsPath?: string;
    enhancementRenditionState?: AudioRenditionState;
}

type ThumbnailRow = {
    thumbnailId: string;
    objectUrl?: string;
    opfsPath: string;
    updatedAt?: number;
    projectId: string;
}

export interface WaveformRow {
    trackId: string;        // PK (1:1 with track)
    projectId: string;      // index for project-wide deletes
    opfsPath: string;       // e.g. /waveforms/<projectId>/<trackId>_l0.dat
    durationSec?: number;   // small, handy to have
    buckets?: number;       // handy
    encoding?: 'peak_u16_v1' | 'minmax_i16_v1';
}

export interface TranscriptRow {
    transcriptId: string;
    projectId: string;
    opfsPath: string;
}

class LocalDB extends Dexie {
    assets!: Table<AssetRow, string>;
    tracks!: Table<TrackRow, string>;
    thumbnails!: Table<ThumbnailRow, string>;
    waveforms!: Table<WaveformRow, string>;
    transcripts!: Table<TranscriptRow, string>;

    constructor() {
        super('LibrettoLocalAssetDatabase');
        this.version(1).stores({
            assets: 'assetId, projectId',
            tracks: 'trackId, projectId',
            thumbnails: 'thumbnailId, projectId',
            waveforms: 'trackId, projectId',
            transcripts: 'transcriptId, projectId',
        });
    }
}

const db = new LocalDB();

// Prevent duplicate rendition extraction when multiple flows resume/process the same track concurrently.
const audioRenditionExtractionInFlight = new Set<string>();
const enhancementRenditionExtractionInFlight = new Set<string>();

// Session-scoped object URL caches (not persisted). Helps avoid flicker caused by
// re-creating new object URLs on re-renders.
const ThumbnailSessionCache: Map<string, string> = new Map();

async function GetRootDir(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

const waveformPath = (projectId: string, trackId: string, denoised: boolean = false) =>
    `/waveforms/${projectId}/${trackId}${denoised ? '_denoised' : ''}.dat`;

// Key for waveform storage - includes denoised suffix if applicable
const waveformKey = (trackId: string, denoised: boolean = false) =>
    denoised ? `${trackId}_denoised` : trackId;

const transcriptPath = (projectId: string, transcriptId: string) =>
    `/transcripts/${projectId}/${transcriptId}.json`;

async function WriteFileToOPFS(path: string, file: File): Promise<FileSystemFileHandle> {
    // path: e.g. /tracks/{id}/original
    console.log("STARTING WRITE FILE TO OPFS:", path, "size:", file.size);
    const segments = path.split('/').filter(Boolean); // remove leading slash
    let dir = await GetRootDir();
    for (let i = 0; i < segments.length - 1; i++) {
        dir = await dir.getDirectoryHandle(segments[i], {create: true});
    }
    const fileName = segments[segments.length - 1];
    const fileHandle = await (dir as FileSystemDirectoryHandle).getFileHandle(fileName, {create: true});
    const writable = await fileHandle.createWritable();
    await file.stream().pipeTo(writable);
    console.log("COMPLETED WRITE FILE TO OPFS:", path);
    return fileHandle;
}

async function pathSegments(path: string): Promise<string[]> {
    return path.split("/").filter(Boolean);
}

export async function DeleteOPFSFile(path: string): Promise<void> {
    const root = await GetRootDir();
    const parts = await pathSegments(path);
    if (parts.length === 0) return;

    const fileName = parts.pop()!;
    let dir = root;
    for (const seg of parts) {
        dir = await dir.getDirectoryHandle(seg, {create: false});
    }
    // If it’s a file, removeEntry works (file not recursive).
    await dir.removeEntry(fileName).catch(() => {/* ignore if already gone */
    });
}

async function GetFileFromOPFS(path: string): Promise<File> {
    const segments = path.split('/').filter(Boolean);
    let dir = await GetRootDir();
    for (let i = 0; i < segments.length - 1; i++) {
        dir = await dir.getDirectoryHandle(segments[i], {create: false});
    }
    const fileName = segments[segments.length - 1];
    const fh = await dir.getFileHandle(fileName, {create: false});
    return await fh.getFile();
}

async function saveTrackSession(trackId: string, sessionUrl: string, totalBytes: number) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const upload = {
        ...(row.upload ?? {}),
        state: 'UPLOADING' as const,
        sessionUrl,
        uploadedBytes: 0,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: 0,
    };
    await db.tracks.update(trackId, {upload, updatedAt: now});
}

async function saveAudioSession(trackId: string, sessionUrl: string, totalBytes: number) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const audioRenditionState: AudioRenditionState = {
        ...(row.audioRenditionState ?? {state: 'UPLOADING', uploadedBytes: 0, totalBytes: 0}),
        state: 'UPLOADING',
        sessionUrl,
        uploadedBytes: 0,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: 0,
    };
    await db.tracks.update(trackId, {audioRenditionState, updatedAt: now});
}

async function saveEnhancementSession(trackId: string, sessionUrl: string, totalBytes: number) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const enhancementRenditionState: AudioRenditionState = {
        ...(row.enhancementRenditionState ?? {state: 'UPLOADING', uploadedBytes: 0, totalBytes: 0}),
        state: 'UPLOADING',
        sessionUrl,
        uploadedBytes: 0,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: 0,
    };
    await db.tracks.update(trackId, {enhancementRenditionState, updatedAt: now});
}

function resumableSessionLooksInvalid(err: any): boolean {
    const status = err?.status;
    // Common codes when a resumable session URL is no longer valid.
    return status === 404 || status === 403 || status === 410;
}

function getErrorStatus(err: any): number | undefined {
    const status = err?.status ?? err?.response?.status;
    return typeof status === 'number' ? status : undefined;
}

function isInitiateNotFoundError(err: any): boolean {
    const status = getErrorStatus(err);
    if (status !== 404) return false;

    const url = String(
        err?.config?.url ||
        err?.request?.responseURL ||
        err?.url ||
        ''
    );

    // If URL is unavailable but status is 404 in this call chain, treat as not found.
    if (!url) return true;
    return url.includes('/initiate/');
}

const INIT_URL_RETRY_DELAY_MS = 250;

async function getInitUrlWithRetry(
    trackId: string,
    kind: string,
    getInitUrlForUpload: (trackId: string, kind: string) => Promise<string>,
): Promise<string> {
    let lastErr: any = undefined;
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const initUrl = await getInitUrlForUpload(trackId, kind);
            const safeInitUrl = String(initUrl || '').trim();
            if (safeInitUrl) {
                return safeInitUrl;
            }

            const missingErr: any = new Error(`Missing init signed URL for ${kind} upload (trackId=${trackId})`);
            missingErr.code = 'MISSING_INIT_URL';
            throw missingErr;
        } catch (e: any) {
            lastErr = e;
            if (!isInitiateNotFoundError(e) || attempt === 1) {
                break;
            }
            await new Promise((resolve) => setTimeout(resolve, INIT_URL_RETRY_DELAY_MS));
        }
    }

    if (isInitiateNotFoundError(lastErr)) {
        const staleErr: any = new Error(`Track not found during upload init after retry (trackId=${trackId}, kind=${kind})`);
        staleErr.code = 'TRACK_INIT_NOT_FOUND';
        staleErr.trackId = trackId;
        staleErr.kind = kind;
        staleErr.cause = lastErr;
        throw staleErr;
    }

    throw lastErr ?? new Error(`Failed to get init signed URL for ${kind} upload (trackId=${trackId})`);
}

async function purgeStaleTrackLocalData(trackId: string, reason: string): Promise<void> {
    const row = await db.tracks.get(trackId) as TrackRow | undefined;
    if (!row) return;

    const paths = [row.opfsPath, row.audioRenditionOpfsPath, row.enhancementRenditionOpfsPath]
        .filter((p): p is string => Boolean(p));

    for (const p of paths) {
        await DeleteOPFSFile(p).catch(() => {});
    }

    const thumbnail = await db.thumbnails.get(trackId).catch(() => undefined);
    if (thumbnail?.opfsPath) {
        await DeleteOPFSFile(thumbnail.opfsPath).catch(() => {});
    }
    await db.thumbnails.delete(trackId).catch(() => {});

    const waveformKeys = [trackId, `${trackId}_denoised`];
    const waveforms = await db.waveforms.bulkGet(waveformKeys).catch(() => []);
    for (const w of waveforms || []) {
        if (w?.opfsPath) {
            await DeleteOPFSFile(w.opfsPath).catch(() => {});
        }
    }
    await db.waveforms.bulkDelete(waveformKeys).catch(() => {});

    await db.tracks.delete(trackId).catch(() => {});

    console.warn('[UploadCleanup] Purged stale local track after repeated initiate not-found', {
        trackId,
        reason,
    });
}

function shouldCleanupAudioRenditionPath(row: TrackRow): boolean {
    const path = String(row.audioRenditionOpfsPath || '').trim();
    if (!path) return false;
    const state = row.audioRenditionState?.state;
    // Legacy rows may not have rendition state persisted.
    if (!state) return true;
    return state === 'SYNCED' || state === 'NOT_NEEDED';
}

function shouldCleanupEnhancementRenditionPath(row: TrackRow): boolean {
    const path = String(row.enhancementRenditionOpfsPath || '').trim();
    if (!path) return false;
    const state = row.enhancementRenditionState?.state;
    // Legacy rows may not have rendition state persisted.
    if (!state) return true;
    return state === 'SYNCED' || state === 'NOT_NEEDED';
}

async function withInitUrlForUpload<T>(
    trackId: string,
    kind: string,
    getInitUrlForUpload: (trackId: string, kind: string) => Promise<string>,
    run: (initUrl: string) => Promise<T>,
): Promise<T | undefined> {
    try {
        const initUrl = await getInitUrlWithRetry(trackId, kind, getInitUrlForUpload);
        return await run(initUrl);
    } catch (e: any) {
        if (e?.code === 'TRACK_INIT_NOT_FOUND') {
            await purgeStaleTrackLocalData(trackId, String(e?.message || 'TRACK_INIT_NOT_FOUND'));
            return undefined;
        }
        throw e;
    }
}

async function markTrackUploadFailed(trackId: string, opts?: { clearSession?: boolean; lastError?: string }): Promise<void> {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const upload: UploadState = {
        ...(row.upload ?? { state: 'NOT_STARTED', uploadedBytes: 0, totalBytes: 0, updatedAt: now, uploadProgressPercent: 0 }),
        state: 'FAILED',
        sessionUrl: opts?.clearSession ? undefined : row.upload?.sessionUrl,
        updatedAt: now,
    };
    await db.tracks.update(trackId, { upload, updatedAt: now });
}

async function markAudioUploadFailed(trackId: string, opts?: { clearSession?: boolean; lastError?: string }): Promise<void> {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const audioRenditionState: AudioRenditionState = {
        ...(row.audioRenditionState ?? { state: 'NOT_STARTED', uploadedBytes: 0, totalBytes: 0, updatedAt: now, uploadProgressPercent: 0 }),
        state: 'FAILED',
        sessionUrl: opts?.clearSession ? undefined : row.audioRenditionState?.sessionUrl,
        updatedAt: now,
    };
    await db.tracks.update(trackId, { audioRenditionState, updatedAt: now });
}

async function markEnhancementUploadFailed(trackId: string, opts?: { clearSession?: boolean; lastError?: string }): Promise<void> {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const enhancementRenditionState: AudioRenditionState = {
        ...(row.enhancementRenditionState ?? { state: 'NOT_STARTED', uploadedBytes: 0, totalBytes: 0, updatedAt: now, uploadProgressPercent: 0 }),
        state: 'FAILED',
        sessionUrl: opts?.clearSession ? undefined : row.enhancementRenditionState?.sessionUrl,
        updatedAt: now,
    };
    await db.tracks.update(trackId, { enhancementRenditionState, updatedAt: now });
}

/** Progress callback that merges into the row.upload object */
async function updateTrackProgress(trackId: string, uploadedBytes: number, totalBytes: number) {
    const now = Date.now();
    const pct = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const upload = {
        ...(row.upload ?? {}),
        state: 'UPLOADING' as const,
        uploadedBytes,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: pct,
    };
    console.log("Updating track upload progress: ", upload.uploadProgressPercent, "%");
    await db.tracks.update(trackId, {upload, updatedAt: now});
}

async function updateAudioProgress(trackId: string, uploadedBytes: number, totalBytes: number) {
    const now = Date.now();
    const pct = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const audioRenditionState: AudioRenditionState = {
        ...(row.audioRenditionState ?? {state: 'UPLOADING', uploadedBytes: 0, totalBytes}),
        state: 'UPLOADING',
        uploadedBytes,
        totalBytes,
        sessionUrl: row.audioRenditionState?.sessionUrl,
        updatedAt: now,
        uploadProgressPercent: pct,
    };
    console.log("Updating audio rendition progress: ", audioRenditionState.uploadProgressPercent, "%");
    await db.tracks.update(trackId, {audioRenditionState, updatedAt: now});
}

async function updateEnhancementProgress(trackId: string, uploadedBytes: number, totalBytes: number) {
    const now = Date.now();
    const pct = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const enhancementRenditionState: AudioRenditionState = {
        ...(row.enhancementRenditionState ?? {state: 'UPLOADING', uploadedBytes: 0, totalBytes}),
        state: 'UPLOADING',
        uploadedBytes,
        totalBytes,
        sessionUrl: row.enhancementRenditionState?.sessionUrl,
        updatedAt: now,
        uploadProgressPercent: pct,
    };
    console.log("Updating enhancement rendition progress: ", enhancementRenditionState.uploadProgressPercent, "%");
    await db.tracks.update(trackId, {enhancementRenditionState, updatedAt: now});
}

/** Mark a track's upload as complete */
async function markTrackUploaded(trackId: string) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const upload = {
        ...(row.upload ?? {}),
        state: 'UPLOADED' as const,
        uploadedBytes: row.upload?.totalBytes ?? 0,
        uploadProgressPercent: 100,
        updatedAt: now,
    };
    await db.tracks.update(trackId, {upload, updatedAt: now});
}

async function markAudioUploaded(trackId: string, totalBytes: number) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const localAudioRenditionPath = String(row.audioRenditionOpfsPath || '').trim();
    const audioRenditionState: AudioRenditionState = {
        ...(row.audioRenditionState ?? {state: 'UPLOADING', uploadedBytes: totalBytes, totalBytes}),
        state: 'SYNCED',
        sessionUrl: undefined,
        uploadedBytes: totalBytes,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: 100,
    };
    await db.tracks.update(trackId, {audioRenditionState, updatedAt: now});

    // Rendition bytes are no longer needed locally once synced to server.
    if (localAudioRenditionPath) {
        await DeleteOPFSFile(localAudioRenditionPath).catch(() => {});
        await db.tracks.update(trackId, {
            audioRenditionOpfsPath: "",
            updatedAt: Date.now(),
        }).catch(() => {});
    }
}

async function markEnhancementUploaded(trackId: string, totalBytes: number) {
    const now = Date.now();
    const row = await db.tracks.get(trackId);
    if (!row) return;
    const localEnhancementRenditionPath = String(row.enhancementRenditionOpfsPath || '').trim();
    const enhancementRenditionState: AudioRenditionState = {
        ...(row.enhancementRenditionState ?? {state: 'UPLOADING', uploadedBytes: totalBytes, totalBytes}),
        state: 'SYNCED',
        sessionUrl: undefined,
        uploadedBytes: totalBytes,
        totalBytes,
        updatedAt: now,
        uploadProgressPercent: 100,
    };
    await db.tracks.update(trackId, {enhancementRenditionState, updatedAt: now});

    // Rendition bytes are no longer needed locally once synced to server.
    if (localEnhancementRenditionPath) {
        await DeleteOPFSFile(localEnhancementRenditionPath).catch(() => {});
        await db.tracks.update(trackId, {
            enhancementRenditionOpfsPath: "",
            updatedAt: Date.now(),
        }).catch(() => {});
    }
}


export async function markAudioRenditionInitialState(trackId: string, shouldExtract: boolean) {
    const state = shouldExtract ? 'NOT_STARTED' : 'NOT_NEEDED';
    await db.tracks.update(trackId, {
        audioRenditionState: {
            state,
            uploadedBytes: 0,
            totalBytes: 0,
            updatedAt: Date.now(),
            uploadProgressPercent: 0,
        },
        updatedAt: Date.now(),
    });
}

export async function markEnhancementRenditionInitialState(trackId: string, shouldExtract: boolean) {
    const state = shouldExtract ? 'NOT_STARTED' : 'NOT_NEEDED';
    await db.tracks.update(trackId, {
        enhancementRenditionState: {
            state,
            uploadedBytes: 0,
            totalBytes: 0,
            updatedAt: Date.now(),
            uploadProgressPercent: 0,
        },
        updatedAt: Date.now(),
    });
}

export async function ensureAudioRenditionProduced(trackId: string, opts?: { replacePrimary?: boolean }) {
    if (audioRenditionExtractionInFlight.has(trackId)) return;
    audioRenditionExtractionInFlight.add(trackId);
    try {
    const t = await db.tracks.get(trackId) as TrackRow | undefined;
    if (!t) return;

    const s = t.audioRenditionState?.state;
    if (s === 'NOT_NEEDED' || s === 'READY_LOCAL' || s === 'UPLOADING' || s === 'SYNCED' || s === 'EXTRACTING') return;

    // Only produce when NOT_STARTED or FAILED
    await db.tracks.update(trackId, {
        audioRenditionState: {
            state: 'EXTRACTING',
            uploadedBytes: 0,
            totalBytes: 0,
            updatedAt: Date.now(),
            uploadProgressPercent: 0
        }
    });

    // Read original from OPFS
    if (!t.opfsPath) {
        await db.tracks.update(trackId, {
            audioRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            }
        });
        return;
    }
    const srcFile = await GetFileFromOPFS(t.opfsPath).catch(() => null);
    if (!srcFile) {
        await db.tracks.update(trackId, {
            audioRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            }
        });
        return;
    }

    try {
        // Your existing extractor (always produces an audio-only rendition)
        const {blob /*, mime, ext */} = await extractOpusAudioRendition(srcFile);

        // Choose a deterministic OPFS path for the rendition (no new DB fields; use trackId)
        const renditionPath = `/tracks/${trackId}/audio-rendition`;
        await WriteFileToOPFS(renditionPath, new File([blob], 'audio-rendition', {type: blob.type}));

        await db.tracks.update(trackId, {
            audioRenditionOpfsPath: renditionPath,
            audioRenditionState: {
                state: 'READY_LOCAL',
                uploadedBytes: 0,
                totalBytes: blob.size,
                sessionUrl: undefined,
                updatedAt: Date.now(),
                uploadProgressPercent: 0,
            },
            updatedAt: Date.now(),
        });

        // Optionally replace the primary local source with the rendition (swap-in-place)
        if (opts?.replacePrimary === true && t.opfsPath) {
            const fileForPrimary = new File([blob], 'original', {type: blob.type});
            await WriteFileToOPFS(t.opfsPath, fileForPrimary);
            // Bump objectUrl for immediate consumers that rely on it
            const replacedFile = await GetFileFromOPFS(t.opfsPath);
            const newUrl = URL.createObjectURL(replacedFile);
            await db.tracks.update(trackId, {objectUrl: newUrl, updatedAt: Date.now()});
            // Emit a lightweight global event so playback can hot-swap if desired
            if (CompositionService.isInitialized()) {
                CompositionService.composition.trigger(EngineInternalEventsEnum.ASSET_SOURCE_REPLACED, {trackId: trackId});
            }
        }
    } catch (e) {
        console.error('audio extract failed', e);
        await db.tracks.update(trackId, {
            audioRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            },
            updatedAt: Date.now(),
        });
        throw e;
    }
    } finally {
        audioRenditionExtractionInFlight.delete(trackId);
    }
}

export async function ensureEnhancementRenditionProduced(trackId: string) {
    if (enhancementRenditionExtractionInFlight.has(trackId)) return;
    enhancementRenditionExtractionInFlight.add(trackId);
    try {
    const t = await db.tracks.get(trackId) as TrackRow | undefined;
    if (!t) return;

    const s = t.enhancementRenditionState?.state;
    if (s === 'NOT_NEEDED' || s === 'READY_LOCAL' || s === 'UPLOADING' || s === 'SYNCED' || s === 'EXTRACTING') return;

    // Only produce when NOT_STARTED or FAILED
    await db.tracks.update(trackId, {
        enhancementRenditionState: {
            state: 'EXTRACTING',
            uploadedBytes: 0,
            totalBytes: 0,
            updatedAt: Date.now(),
            uploadProgressPercent: 0
        }
    });

    // Read original from OPFS
    if (!t.opfsPath) {
        await db.tracks.update(trackId, {
            enhancementRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            }
        });
        return;
    }
    const srcFile = await GetFileFromOPFS(t.opfsPath).catch(() => null);
    if (!srcFile) {
        await db.tracks.update(trackId, {
            enhancementRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            }
        });
        return;
    }

    try {
        const {blob} = await extractEnhancementAudioRendition(srcFile);

        const renditionPath = `/tracks/${trackId}/enhancement-rendition`;
        await WriteFileToOPFS(renditionPath, new File([blob], 'enhancement-rendition', {type: blob.type}));

        await db.tracks.update(trackId, {
            enhancementRenditionOpfsPath: renditionPath,
            enhancementRenditionState: {
                state: 'READY_LOCAL',
                uploadedBytes: 0,
                totalBytes: blob.size,
                sessionUrl: undefined,
                updatedAt: Date.now(),
                uploadProgressPercent: 0,
            },
            updatedAt: Date.now(),
        });

    } catch (e) {
        console.error('enhancement extract failed', e);
        await db.tracks.update(trackId, {
            enhancementRenditionState: {
                state: 'FAILED',
                uploadedBytes: 0,
                totalBytes: 0,
                updatedAt: Date.now(),
                uploadProgressPercent: 0
            },
            updatedAt: Date.now(),
        });
        throw e;
    }
    } finally {
        enhancementRenditionExtractionInFlight.delete(trackId);
    }
}


/**
 * Kicks off background upload for a track (creates a session if needed, persists it,
 * streams chunks, and updates Dexie on progress).
 */
export async function startOrResumeTrackUpload({
                                                   trackId,
                                                   initSignedUrl,
                                               }: {
    trackId: string;
    initSignedUrl: string; // POST URL from your API to start GCS resumable
}) {
    void requestPersistentStorageOnce('upload');
    const row = await db.tracks.get(trackId);
    if (!row?.opfsPath) throw new Error('No local file for track');

    // Get the File from OPFS (source-of-truth for upload bytes)
    const file = await GetFileFromOPFS(row.opfsPath);
    if (!file) throw new Error('Local file missing');
    const contentType = (row.mimeType && row.mimeType.trim() !== '') ? row.mimeType.trim() : (file.type || 'application/octet-stream');

    // Either reuse an existing session or create a new one
    let sessionUrl = row.upload?.sessionUrl;
    let didResetSession = false;

    while (true) {
        if (!sessionUrl) {
            const safeInitSignedUrl = String(initSignedUrl || "").trim();
            if (!safeInitSignedUrl) {
                throw new Error(`Missing init signed URL for original upload (trackId=${trackId})`);
            }
            sessionUrl = await InitiateGcsResumable(safeInitSignedUrl, file, contentType); // returns Location header
            await saveTrackSession(trackId, sessionUrl, file.size);
        }

        // Probe current server offset so we can compute uploadedBytes immediately
        let offset = 0;
        try {
            offset = await GetGcsServerOffset(sessionUrl, file.size);
            await updateTrackProgress(trackId, offset, file.size);
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markTrackUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await db.tracks.update(trackId, { updatedAt: Date.now() }).catch(() => {});
            throw e;
        }

        try {
            // Stream chunks; on each loop, our callback will persist progress to Dexie
            await UploadGcsResumable(
                sessionUrl,
                file,
                async (pct) => {
                    const trackRow = await db.tracks.get(trackId);
                    const trackExists = !!trackRow;
                    if (!trackExists) return false;

                    // Convert pct → bytes for a consistent store view
                    const uploadedBytes = Math.floor((pct / 100) * file.size);
                    void updateTrackProgress(trackId, uploadedBytes, file.size);

                    return true;
                },
                offset,
                contentType,
            );
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markTrackUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await markTrackUploadFailed(trackId, { clearSession: false, lastError: e?.message }).catch(() => {});
            throw e;
        }

        await markTrackUploaded(trackId);
        break;
    }
}

export async function startOrResumeAudioRenditionUpload({
                                                            trackId,
                                                            initSignedUrlForRendition, // POST URL from your API to start GCS resumable for the rendition object
                                                        }: {
    trackId: string;
    initSignedUrlForRendition: string;
}) {
    void requestPersistentStorageOnce('upload');
    const row = await db.tracks.get(trackId);
    if (!row?.audioRenditionOpfsPath) throw new Error('No local audio rendition');

    const file = await GetFileFromOPFS(row.audioRenditionOpfsPath);
    if (!file) throw new Error('Local audio rendition missing');
    const contentType = file.type || 'application/octet-stream';

    // Reuse existing session or create one
    let sessionUrl = row.audioRenditionState?.sessionUrl;
    let didResetSession = false;

    while (true) {
        if (!sessionUrl) {
            const safeInitSignedUrl = String(initSignedUrlForRendition || "").trim();
            if (!safeInitSignedUrl) {
                throw new Error(`Missing init signed URL for audio rendition upload (trackId=${trackId})`);
            }
            sessionUrl = await InitiateGcsResumable(safeInitSignedUrl, file, contentType);
            await saveAudioSession(trackId, sessionUrl, file.size);
        } else {
            // ensure we reflect UPLOADING state
            await saveAudioSession(trackId, sessionUrl, file.size);
        }

        // Probe current server offset
        let offset = 0;
        try {
            offset = await GetGcsServerOffset(sessionUrl, file.size);
            await updateAudioProgress(trackId, offset, file.size);
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markAudioUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await db.tracks.update(trackId, { updatedAt: Date.now() }).catch(() => {});
            throw e;
        }

        try {
            // Stream chunks; update Dexie with bytes as you go
            await UploadGcsResumable(
                sessionUrl,
                file,
                async (pct) => {
                    const trackRow = await db.tracks.get(trackId);
                    const trackExists = !!trackRow;
                    if (!trackExists) return false;

                    const uploadedBytes = Math.floor((pct / 100) * file.size);
                    void updateAudioProgress(trackId, uploadedBytes, file.size);

                    return true;
                },
                offset,
                contentType,
            );
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markAudioUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await markAudioUploadFailed(trackId, { clearSession: false, lastError: e?.message }).catch(() => {});
            throw e;
        }

        await markAudioUploaded(trackId, file.size);
        break;
    }
}

export async function startOrResumeEnhancementRenditionUpload({
                                                                   trackId,
                                                                   initSignedUrlForRendition, // POST URL from your API to start GCS resumable for the enhancement object
                                                               }: {
    trackId: string;
    initSignedUrlForRendition: string;
}) {
    void requestPersistentStorageOnce('upload');
    const row = await db.tracks.get(trackId);
    if (!row?.enhancementRenditionOpfsPath) throw new Error('No local enhancement rendition');

    const file = await GetFileFromOPFS(row.enhancementRenditionOpfsPath);
    if (!file) throw new Error('Local enhancement rendition missing');
    const contentType = file.type || 'application/octet-stream';

    // Reuse existing session or create one
    let sessionUrl = row.enhancementRenditionState?.sessionUrl;
    let didResetSession = false;

    while (true) {
        if (!sessionUrl) {
            const safeInitSignedUrl = String(initSignedUrlForRendition || "").trim();
            if (!safeInitSignedUrl) {
                throw new Error(`Missing init signed URL for enhancement rendition upload (trackId=${trackId})`);
            }
            sessionUrl = await InitiateGcsResumable(safeInitSignedUrl, file, contentType);
            await saveEnhancementSession(trackId, sessionUrl, file.size);
        } else {
            // ensure we reflect UPLOADING state
            await saveEnhancementSession(trackId, sessionUrl, file.size);
        }

        // Probe current server offset
        let offset = 0;
        try {
            offset = await GetGcsServerOffset(sessionUrl, file.size);
            await updateEnhancementProgress(trackId, offset, file.size);
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markEnhancementUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await db.tracks.update(trackId, { updatedAt: Date.now() }).catch(() => {});
            throw e;
        }

        try {
            // Stream chunks; update Dexie with bytes as you go
            await UploadGcsResumable(
                sessionUrl,
                file,
                async (pct) => {
                    const trackRow = await db.tracks.get(trackId);
                    const trackExists = !!trackRow;
                    if (!trackExists) return false;

                    const uploadedBytes = Math.floor((pct / 100) * file.size);
                    void updateEnhancementProgress(trackId, uploadedBytes, file.size);

                    return true;
                },
                offset,
                contentType,
            );
        } catch (e) {
            if (!didResetSession && resumableSessionLooksInvalid(e)) {
                didResetSession = true;
                await markEnhancementUploadFailed(trackId, { clearSession: true, lastError: e?.message });
                sessionUrl = undefined;
                continue;
            }
            await markEnhancementUploadFailed(trackId, { clearSession: false, lastError: e?.message }).catch(() => {});
            throw e;
        }

        await markEnhancementUploaded(trackId, file.size);
        break;
    }
}

const RENDITION_EXTRACTION_POLL_MS = 150;
const RENDITION_EXTRACTION_TIMEOUT_MS = 2 * 60 * 1000;

async function waitForRenditionExtractionToSettle(
    trackId: string,
    kind: 'audio' | 'enhancement'
): Promise<TrackRow | undefined> {
    const startedAt = Date.now();
    while (true) {
        const latest = await db.tracks.get(trackId) as TrackRow | undefined;
        if (!latest) return undefined;

        const state = kind === 'audio'
            ? latest.audioRenditionState?.state
            : latest.enhancementRenditionState?.state;
        const inFlight = kind === 'audio'
            ? audioRenditionExtractionInFlight.has(trackId)
            : enhancementRenditionExtractionInFlight.has(trackId);

        if (!inFlight && state !== 'EXTRACTING') {
            return latest;
        }

        if (Date.now() - startedAt >= RENDITION_EXTRACTION_TIMEOUT_MS) {
            console.warn(`[ProcessAndUploadTracks] Timed out waiting for ${kind} rendition extraction`, { trackId, state, inFlight });
            return latest;
        }

        await new Promise((resolve) => setTimeout(resolve, RENDITION_EXTRACTION_POLL_MS));
    }
}

async function ProcessAndUploadTracks(
    trackId: string,
    getInitUrlForUpload: (trackId: string, kind: string) => Promise<string>) {
    const t = await db.tracks.get(trackId) as TrackRow | undefined;
    if (!t) return;

    // 1) AUDIO RENDITION (if needed)
    if (t.audioRenditionState && t.audioRenditionState.state !== 'NOT_NEEDED' && t.audioRenditionState.state !== 'SYNCED') {
        try {
            await ensureAudioRenditionProduced(trackId);
        } catch (e) {
            console.warn('[ProcessAndUploadTracks] ensureAudioRenditionProduced failed:', trackId, e);
        }
        const latest = await waitForRenditionExtractionToSettle(trackId, 'audio');
        const audioState = latest?.audioRenditionState?.state;
        const hasLocalAudioRendition = Boolean(latest?.audioRenditionOpfsPath);
        if (hasLocalAudioRendition && (audioState === 'READY_LOCAL' || audioState === 'UPLOADING' || audioState === 'FAILED')) {
            const audioResult = await withInitUrlForUpload(
                trackId,
                "audio-rendition",
                getInitUrlForUpload,
                async (initUrl) => {
                    await startOrResumeAudioRenditionUpload({trackId, initSignedUrlForRendition: initUrl});
                    return true;
                }
            );
            if (!audioResult) return;
        }
    }

    // 2) ENHANCEMENT RENDITION (if needed)
    const t2 = await db.tracks.get(trackId);
    if (!t2) return;
    if (t2.enhancementRenditionState && t2.enhancementRenditionState.state !== 'NOT_NEEDED' && t2.enhancementRenditionState.state !== 'SYNCED') {
        try {
            await ensureEnhancementRenditionProduced(trackId);
        } catch (e) {
            console.warn('[ProcessAndUploadTracks] ensureEnhancementRenditionProduced failed:', trackId, e);
        }
        const latest = await waitForRenditionExtractionToSettle(trackId, 'enhancement');
        const enhancementState = latest?.enhancementRenditionState?.state;
        const hasLocalEnhancementRendition = Boolean(latest?.enhancementRenditionOpfsPath);
        if (hasLocalEnhancementRendition && (enhancementState === 'READY_LOCAL' || enhancementState === 'UPLOADING' || enhancementState === 'FAILED')) {
            const enhancementResult = await withInitUrlForUpload(
                trackId,
                "enhancement-rendition",
                getInitUrlForUpload,
                async (initUrl) => {
                    await startOrResumeEnhancementRenditionUpload({trackId, initSignedUrlForRendition: initUrl});
                    return true;
                }
            );
            if (!enhancementResult) return;
        }
    }

    // 3) ORIGINAL MEDIA
    const t3 = await db.tracks.get(trackId);
    if (!t3) return;

    // Upload-only rows are temporary local copies used just for resumable upload. Once uploaded,
    // we delete both the OPFS file and the Dexie row so they never affect normal playback.
    if (t3.uploadOnly && t3.upload?.state === 'UPLOADED') {
        await DeleteOPFSFile(t3.opfsPath).catch(() => {});
        await db.tracks.delete(trackId).catch(() => {});
        return;
    }

    if (t3.upload?.state !== 'UPLOADED') {
        const kind = t3.uploadOnly ? "server-first" : "track";
        const trackUploadResult = await withInitUrlForUpload(
            trackId,
            kind,
            getInitUrlForUpload,
            async (initUrl) => {
                await startOrResumeTrackUpload({trackId, initSignedUrl: initUrl});
                return true;
            }
        );
        if (!trackUploadResult) return;
    }

    // Cleanup after successful upload for upload-only rows.
    // Server-first rows intentionally skip client-side rendition processing.
    const t4 = await db.tracks.get(trackId);
    if (t4?.uploadOnly && t4.upload?.state === 'UPLOADED') {
        await DeleteOPFSFile(t4.opfsPath).catch(() => {});
        await db.tracks.delete(trackId).catch(() => {});
        return;
    }
}

export async function ProcessAndUploadTrackNow(
    trackId: string, getInitUrlForUpload: (trackId: string, kind: string) => Promise<string>, userId: any) {
    await ProcessAndUploadTracks(trackId, getInitUrlForUpload);
}


export async function ResumePendingUploadsAndProcessingsOnInit(
    getInitUrlForUpload: (trackId: string, kind: string) => Promise<string>,
    userId: string
) {
    const all = await db.tracks.toArray();

    // Cleanup legacy/leftover local rendition bytes that are already synced or not needed.
    const renditionCleanupCandidates = all.filter((r) =>
        r &&
        r.userId === userId &&
        r.localPresent &&
        (shouldCleanupAudioRenditionPath(r) || shouldCleanupEnhancementRenditionPath(r))
    );
    if (renditionCleanupCandidates.length > 0) {
        let cleanedAudio = 0;
        let cleanedEnhancement = 0;
        await Promise.all(renditionCleanupCandidates.map(async (r) => {
            const updates: any = { updatedAt: Date.now() };

            if (shouldCleanupAudioRenditionPath(r)) {
                const path = String(r.audioRenditionOpfsPath || '').trim();
                if (path) {
                    await DeleteOPFSFile(path).catch(() => {});
                }
                updates.audioRenditionOpfsPath = "";
                cleanedAudio += 1;
            }

            if (shouldCleanupEnhancementRenditionPath(r)) {
                const path = String(r.enhancementRenditionOpfsPath || '').trim();
                if (path) {
                    await DeleteOPFSFile(path).catch(() => {});
                }
                updates.enhancementRenditionOpfsPath = "";
                cleanedEnhancement += 1;
            }

            await db.tracks.update(r.trackId, updates).catch(() => {});
        }));
        if (cleanedAudio > 0 || cleanedEnhancement > 0) {
            console.log('[UploadCleanup] Removed stale local rendition files', {
                cleanedAudio,
                cleanedEnhancement,
            });
        }
    }

    // Cleanup any completed upload-only rows that may have been left behind (e.g., crash after upload).
    const uploadOnlyUploaded = all.filter(r =>
        r &&
        r.uploadOnly === true &&
        r.userId === userId &&
        r.localPresent &&
        r.upload?.state === 'UPLOADED'
    );
    const cleanedIds = new Set<string>(uploadOnlyUploaded.map(r => r.trackId));
    if (uploadOnlyUploaded.length > 0) {
        await Promise.all(uploadOnlyUploaded.map(async (r) => {
            await DeleteOPFSFile(r.opfsPath).catch(() => {});
            await db.tracks.delete(r.trackId).catch(() => {});
        }));
    }

    const pending = all.filter(r =>
        !cleanedIds.has(r.trackId) &&
        r.localPresent &&
        r.userId === userId &&
        (
            r.upload?.state !== 'UPLOADED' ||
            (r.audioRenditionState && r.audioRenditionState.state !== 'NOT_NEEDED' && r.audioRenditionState.state !== 'SYNCED') ||
            (r.enhancementRenditionState && r.enhancementRenditionState.state !== 'NOT_NEEDED' && r.enhancementRenditionState.state !== 'SYNCED')
        )
    );

    const concurrency = 2;
    const queue = [...pending.map(p => p.trackId)];
    const workers: Promise<void>[] = [];

    for (let i = 0; i < concurrency; i++) {
        workers.push((async () => {
            while (queue.length) {
                const id = queue.shift()!;
                try {
                    await ProcessAndUploadTracks(id, getInitUrlForUpload);
                } catch (e) {
                    console.error('processTrackUploads error', id, e);
                }
            }
        })());
    }
    await Promise.all(workers);
}

class LocalAssetStoreInstance {

    public CreateAsset = async ({assetId, userId, projectId, title, file, createTime}) => {
        void requestPersistentStorageOnce('upload');

        const opfsPath = `/assets/${assetId}/original`;
        await WriteFileToOPFS(opfsPath, file);
        const now = Date.now();

        const assetRow = {
            assetId,
            createTime: typeof createTime === "number" ? createTime : +createTime, // ensure number
            projectId,
            title,
            localPresent: true,
            opfsPath: opfsPath,
            userId,
            upload: {
                state: "NOT_STARTED",
                uploadedBytes: 0,
                totalBytes: file.size,
                updatedAt: now,
                uploadProgressPercent: 0,
            },
            updatedAt: now,
        } satisfies AssetRow;

        await db.assets.put(assetRow);

        const localFile = await GetFileFromOPFS(opfsPath);
        const objectUrl = URL.createObjectURL(localFile);

        await db.tracks.update(assetId, {objectUrl, updatedAt: Date.now()});

        return {objectUrl};
    }

    public CreateTrack = async ({trackId, userId, assetId, projectId, title, file, createTime}: {
        trackId: string;
        assetId: string;
        projectId: string;
        title: string;
        userId: string;
        file: File;
        createTime: number | Date;
    }) => {
        void requestPersistentStorageOnce('upload');
        const opfsPath = `/tracks/${trackId}/original`;
        await WriteFileToOPFS(opfsPath, file);
        const now = Date.now();

        // Preserve the original MIME type and filename for proper upload
        const mimeType = file.type || 'application/octet-stream';
        const originalFileName = file.name || `${trackId}.bin`;

        const trackRow = {
            assetId,
            trackId,
            createTime: typeof createTime === "number" ? createTime : +createTime, // ensure number
            projectId,
            title,
            userId,
            localPresent: true,
            opfsPath: opfsPath,
            mimeType,
            originalFileName,
            upload: {
                state: "NOT_STARTED",
                uploadedBytes: 0,
                totalBytes: file.size,
                updatedAt: now,
                uploadProgressPercent: 0,
            },
            updatedAt: now,
        } satisfies TrackRow;

        await db.tracks.put(trackRow);

        const localFile = await GetFileFromOPFS(opfsPath);
        const objectUrl = URL.createObjectURL(localFile);

        await db.tracks.update(trackId, {objectUrl, updatedAt: Date.now()});

        return {objectUrl};
    }

    public CreateUploadOnlyTrack = async ({trackId, userId, assetId, projectId, title, file, createTime}: {
        trackId: string;
        assetId: string;
        projectId: string;
        title: string;
        userId: string;
        file: File;
        createTime: number | Date;
    }) => {
        void requestPersistentStorageOnce('upload');

        const opfsPath = `/tracks/${trackId}/server-first`;
        await WriteFileToOPFS(opfsPath, file);
        const now = Date.now();

        const mimeType = file.type || 'application/octet-stream';
        const originalFileName = file.name || `${trackId}.bin`;

        const trackRow = {
            assetId,
            trackId,
            createTime: typeof createTime === "number" ? createTime : +createTime, // ensure number
            projectId,
            title,
            userId,
            localPresent: true,
            opfsPath,
            uploadOnly: true,
            mimeType,
            originalFileName,
            upload: {
                state: "NOT_STARTED",
                uploadedBytes: 0,
                totalBytes: file.size,
                updatedAt: now,
                uploadProgressPercent: 0,
            },
            updatedAt: now,
        } satisfies TrackRow;

        await db.tracks.put(trackRow);

        return {opfsPath};
    }

    public SaveThumbnail = async ({thumbnailId, projectId, file}) => {
        const opfsPath = `/thumbnails/${thumbnailId}/original`;
        await WriteFileToOPFS(opfsPath, file);

        await db.thumbnails.put({thumbnailId, projectId, opfsPath});

        const localFile = await GetFileFromOPFS(opfsPath);
        const objectUrl = URL.createObjectURL(localFile);

        await db.thumbnails.update(thumbnailId, {objectUrl, updatedAt: Date.now()});

        // Store in session cache so subsequent reads in this session reuse same URL
        ThumbnailSessionCache.set(thumbnailId, objectUrl);

        return {objectUrl};
    }

    public GetThumbnail = async ({thumbnailId}) => {
        // 1) Fast path: session cache
        const cached = ThumbnailSessionCache.get(thumbnailId);
        if (cached) return cached;

        const row = await db.thumbnails.get(thumbnailId);
        if (!row?.opfsPath) return undefined;

        const file = await GetFileFromOPFS(row.opfsPath);
        if (!file || file.size === 0) return undefined;   // guard against partial writes

        const url = URL.createObjectURL(file);
        ThumbnailSessionCache.set(thumbnailId, url);
        return url; // do NOT persist this back to Dexie
    };

    public SaveAudioWaveformData = async ({
                                              trackId,
                                              projectId,
                                              datBuf,
                                              durationSec,
                                              buckets,
                                              encoding,
                                              denoised = false,
                                          }: {
        trackId: string;
        projectId: string;
        datBuf: ArrayBuffer;
        durationSec?: number;
        buckets?: number;
        encoding?: 'peak_u16_v1' | 'minmax_i16_v1';
        denoised?: boolean;
    }) => {
        const key = waveformKey(trackId, denoised);
        const path = waveformPath(projectId, trackId, denoised);
        const fileName = `${trackId}${denoised ? '_denoised' : ''}.dat`;
        const file = new File([datBuf], fileName, {type: 'application/octet-stream'});

        // 1) Write bytes to OPFS
        await WriteFileToOPFS(path, file);

        // 2) Upsert pointer + tiny metadata (use key which includes _denoised suffix)
        await db.waveforms.put({
            trackId: key,
            projectId,
            opfsPath: path,
            durationSec,
            buckets,
            encoding,
        });
    }

    public GetAudioWaveformData = async ({trackId, denoised = false}: {trackId: string; denoised?: boolean}) => {
        const key = waveformKey(trackId, denoised);
        const row = await db.waveforms.get(key);
        if (!row) return undefined;

        try {
            const file = await GetFileFromOPFS(row.opfsPath);
            return file.arrayBuffer();
        } catch {
            // OPFS file is gone; tidy up the pointer
            await db.waveforms.delete(key);
            return undefined;
        }
    }

    public GetTranscript = async ({transcriptId}: {transcriptId: string}) => {
        const row = await db.transcripts.get(transcriptId);
        if (!row) return undefined;

        try {
            const file = await GetFileFromOPFS(row.opfsPath);
            return file.arrayBuffer();
        } catch {
            // OPFS file is gone; tidy up the pointer
            await db.transcripts.delete(transcriptId);
            return undefined;
        }
    }

    public SaveTranscript = async ({transcriptId, projectId, transcriptJson}: {transcriptId: string, projectId: string, transcriptJson: any}) => {
        if (!transcriptId) return;
        const opfsPath = transcriptPath(projectId, transcriptId);
        const json = JSON.stringify(transcriptJson);
        const file = new File([json], `${transcriptId}.json`, {type: 'application/json'});
        await WriteFileToOPFS(opfsPath, file);
        await db.transcripts.put({transcriptId, projectId, opfsPath});
    }

    public GetTrackSource = async ({trackId}) => {
        const row = await db.tracks.get(trackId);
        if (row?.uploadOnly) return undefined;
        if (!row?.opfsPath) return undefined;

        const file = await GetFileFromOPFS(row.opfsPath);
        if (!file || file.size === 0) return undefined;   // guard against partial writes

        return URL.createObjectURL(file); // do NOT persist this back to Dexie
    }

    public GetAssetSource = async ({assetId}) => {
        const row = await db.assets.get(assetId);
        if (!row?.opfsPath) return undefined;

        const file = await GetFileFromOPFS(row.opfsPath);
        if (!file || file.size === 0) return undefined;   // guard against partial writes

        return URL.createObjectURL(file); // do NOT persist this back to Dexie
    }

    public async DeleteProjectWaveforms({projectId}: { projectId: string }) {
        const rows = await db.waveforms.where('projectId').equals(projectId).toArray();
        for (const r of rows) {
            await DeleteOPFSFile(r.opfsPath).catch(() => {
            });
        }
        await db.waveforms.bulkDelete(rows.map(r => r.trackId));
    }

    public async DeleteProject({projectId}: { projectId: string }) {
        // 1) Read rows first (we need their opfsPath values)
        const [assets, tracks] = await Promise.all([
            db.assets.where("projectId").equals(projectId).toArray(),
            db.tracks.where("projectId").equals(projectId).toArray(),
        ]);

        // 2) Collect unique OPFS paths
        const filePaths = new Set<string>();
        for (const a of assets) if (a.opfsPath) filePaths.add(a.opfsPath);
        for (const t of tracks) {
            if (t.opfsPath) filePaths.add(t.opfsPath);
            if (t.audioRenditionOpfsPath) filePaths.add(t.audioRenditionOpfsPath);
            if (t.enhancementRenditionOpfsPath) filePaths.add(t.enhancementRenditionOpfsPath);
        }

        // 3) Best-effort delete files (don’t let one failure abort entire cleanup)
        await Promise.all(
            Array.from(filePaths).map(async (p) => {
                try {
                    await DeleteOPFSFile(p);      // or await deleteOpfsDir(p) if you keep folders per item
                } catch (e) {
                    // log and continue
                    console.warn("OPFS delete failed for", p, e);
                }
            })
        );

        // 4) Delete rows in a Dexie transaction
        await db.transaction("rw", db.assets, db.tracks, async () => {
            await db.assets.where("projectId").equals(projectId).delete();
            await db.tracks.where("projectId").equals(projectId).delete();
        });

        await this.DeleteProjectWaveforms({projectId});
    }
}

const LocalAssetStore = new LocalAssetStoreInstance();

/**
 * Check if a track exists locally in Dexie with a valid OPFS path.
 * This is a quick check that doesn't load the actual file content.
 * @returns true if track is locally available, false otherwise
 */
export async function IsTrackLocallyAvailable(trackId: string): Promise<boolean> {
    if (!trackId) return false;
    try {
        const row = await db.tracks.get(trackId);
        return !!(row?.localPresent && row?.opfsPath && !row?.uploadOnly);
    } catch {
        return false;
    }
}

/**
 * Best-effort check for pending local-first uploads for the current user.
 * Used for unload guards / reliability UX.
 */
export async function HasPendingLocalAssetUploadsForUser(userId: string): Promise<boolean> {
    if (!userId || typeof userId !== 'string') return false;
    try {
        const all = await db.tracks.toArray();
        for (const r of all) {
            if (!r || r.userId !== userId) continue;
            if (!r.localPresent) continue;

            const uploadDone = r.upload?.state === 'UPLOADED';
            const audioDone =
                !r.audioRenditionState ||
                r.audioRenditionState.state === 'NOT_NEEDED' ||
                r.audioRenditionState.state === 'SYNCED';
            const enhancementDone =
                !r.enhancementRenditionState ||
                r.enhancementRenditionState.state === 'NOT_NEEDED' ||
                r.enhancementRenditionState.state === 'SYNCED';

            if (!uploadDone || !audioDone || !enhancementDone) return true;
        }
    } catch {
        // ignore
    }
    return false;
}

/**
 * Check local availability for multiple tracks at once.
 * Returns a Map of trackId -> isLocallyAvailable
 */
export async function GetTracksLocalAvailability(trackIds: string[]): Promise<Map<string, boolean>> {
    const result = new Map<string, boolean>();
    if (!trackIds.length) return result;
    
    try {
        const rows = await db.tracks.bulkGet(trackIds);
        for (let i = 0; i < trackIds.length; i++) {
            const row = rows[i];
            result.set(trackIds[i], !!(row?.localPresent && row?.opfsPath && !row?.uploadOnly));
        }
    } catch {
        // On error, mark all as not locally available
        for (const id of trackIds) {
            result.set(id, false);
        }
    }
    return result;
}

export type TrackUploadSnapshot = {
    state: 'NOT_STARTED' | 'UPLOADING' | 'UPLOADED' | 'FAILED' | 'MISSING';
    uploadedBytes: number;
    totalBytes: number;
    uploadProgressPercent: number;
};

/**
 * Read original-media upload progress for a set of tracks.
 * Enhancement/audio-rendition progress is intentionally excluded.
 */
export async function GetTracksUploadSnapshots(trackIds: string[]): Promise<Map<string, TrackUploadSnapshot>> {
    const result = new Map<string, TrackUploadSnapshot>();
    if (!trackIds.length) return result;

    const uniqueTrackIds = Array.from(new Set(trackIds.map((id) => String(id || "").trim()).filter(Boolean)));
    if (!uniqueTrackIds.length) return result;

    try {
        const rows = await db.tracks.bulkGet(uniqueTrackIds);
        for (let i = 0; i < uniqueTrackIds.length; i += 1) {
            const trackId = uniqueTrackIds[i];
            const row = rows[i];

            if (!row || row.uploadOnly) {
                result.set(trackId, {
                    state: 'MISSING',
                    uploadedBytes: 0,
                    totalBytes: 0,
                    uploadProgressPercent: 0,
                });
                continue;
            }

            const uploadedBytes = Math.max(0, Number(row.upload?.uploadedBytes ?? 0) || 0);
            const totalBytes = Math.max(0, Number(row.upload?.totalBytes ?? 0) || 0);
            const state = (row.upload?.state ?? 'NOT_STARTED') as TrackUploadSnapshot['state'];
            const rawProgress = Number(row.upload?.uploadProgressPercent ?? 0);
            const computedProgress = totalBytes > 0
                ? Math.min(100, Math.max(0, Math.round((uploadedBytes / totalBytes) * 100)))
                : 0;
            const uploadProgressPercent = state === 'UPLOADED'
                ? 100
                : Number.isFinite(rawProgress)
                    ? Math.min(100, Math.max(0, Math.round(rawProgress)))
                    : computedProgress;

            result.set(trackId, {
                state,
                uploadedBytes,
                totalBytes,
                uploadProgressPercent,
            });
        }
    } catch {
        for (const trackId of uniqueTrackIds) {
            result.set(trackId, {
                state: 'MISSING',
                uploadedBytes: 0,
                totalBytes: 0,
                uploadProgressPercent: 0,
            });
        }
    }

    return result;
}

export {LocalAssetStore};
